import { TechRadarPage } from '@backstage-community/plugin-tech-radar'
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs'
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin
} from '@backstage/plugin-catalog'
import { CatalogImportPage, catalogImportPlugin } from '@backstage/plugin-catalog-import'
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities'
import { HomepageCompositionRoot } from '@backstage/plugin-home'
import { orgPlugin } from '@backstage/plugin-org'
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder'
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react'
import { SearchPage } from '@backstage/plugin-search'
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage
} from '@backstage/plugin-techdocs'
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib'
import { TechDocsAddons } from '@backstage/plugin-techdocs-react'
import { UserSettingsPage } from '@backstage/plugin-user-settings'
import { Route } from 'react-router-dom'
import { apis, pluginApiOverrides } from './apis'
import { CustomCatalogIndexPage } from './components/catalog-index/CustomCatalogIndexPage'
import { entityPage } from './components/catalog/EntityPage'
import { HomePage } from './components/home/HomePage'
import { Root } from './components/Root'
import { searchPage } from './components/search/SearchPage'
import { techDocsPage } from './components/techdocs/TechDocsPage'
import { AITechRadarPage } from './custom/techRadar/AITechRadarPage'
import {
  ComponentNameFieldExtension,
  ComponentTypeFieldExtension,
  SennderGitlabGroupFieldExtension
} from './scaffolder/extensions'

import { convertLegacyAppOptions, convertLegacyAppRoot } from '@backstage/core-compat-api'
import { AppRouter, FlatRoutes } from '@backstage/core-app-api'
import { createApp } from '@backstage/frontend-defaults'
import appVisualizerPlugin from '@backstage/plugin-app-visualizer'
import authPlugin from '@backstage/plugin-auth'
import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components'
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph'
import { RequirePermission } from '@backstage/plugin-permission-react'

import { badgesPlugin } from '@backstage-community/plugin-badges'
import { SignInPage } from '@backstage/core-components'
import { configApiRef, oktaAuthApiRef, useApi } from '@backstage/core-plugin-api'
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha'

import { FuzzyRedirect } from './components/router/FuzzyRedirect'

// plug-in: OpsGenie
import { OpsgeniePage } from '@k-phoen/backstage-plugin-opsgenie'

// support MermaidJS
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid'

// custom theme
import { UnifiedThemeProvider } from '@backstage/theme'
import { FrontendExternalDocsPage } from '@internal/plugin-frontend-external-docs'
import { QaTestResultsPage } from '@internal/plugin-qa-test-results'
import { customDarkTheme, customLightTheme } from './theme/customTheme'

import { TechDocsRedirectExtension } from './custom/techdocsRedirect'
import { TechDocsShadowBodyFix } from './custom/techdocsShadowBodyFix'

import { PlausibleAnalyticsPage } from './components/plausible/PlausibleAnalyticsPage'

import { McpChatPage } from '@backstage-community/plugin-mcp-chat'
import { ContribPage } from '@internal/plugin-contrib'
import { DatadogServiceCatalogPage } from '@internal/plugin-datadog-service-catalog'
import { DoraPage } from '@internal/plugin-dora'
import { TasksObservabilityPage } from '@internal/plugin-tasks-observability'
import { TestingMetricsPage } from '@internal/plugin-testing-metrics'
import { techInsightsOverviewPage } from './components/tech-insights/TechInsightsOverviewPage'
import { CustomReviewStep } from './scaffolder/CustomReviewStep/index'

// backend auth
const oktaAuthProvider = {
  id: 'okta-auth-provider',
  title: 'Okta',
  message: 'Sign-in using OktaSSO',
  apiRef: oktaAuthApiRef
}

const convertedOptions = convertLegacyAppOptions({
  apis,
  plugins: [badgesPlugin],
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={
          useApi(configApiRef).getString('auth.environment') === 'local'
            ? ['guest', oktaAuthProvider]
            : [oktaAuthProvider]
        }
      />
    )
  },
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customLightTheme}>{children}</UnifiedThemeProvider>
      )
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customDarkTheme}>{children}</UnifiedThemeProvider>
      )
    }
  ]
})

const paths = {
  catalogEntity: '/catalog/:namespace/:kind/:name',
  techDocsPage: '/docs/:namespace/:kind/:name/*'
}

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogIndexPage actions={[]} />
    </Route>
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path={paths.catalogEntity} element={<CatalogEntityPage />}>
      <FuzzyRedirect path={paths.catalogEntity}>{entityPage}</FuzzyRedirect>
    </Route>
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route path={paths.techDocsPage} element={<TechDocsReaderPage />}>
      <FuzzyRedirect path={paths.techDocsPage}>
        {techDocsPage}
        <TechDocsAddons>
          <ReportIssue />
          <Mermaid
            config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }}
          />
          <TechDocsRedirectExtension />
          <TechDocsShadowBodyFix />
        </TechDocsAddons>
      </FuzzyRedirect>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            ReviewStepComponent: CustomReviewStep
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <SennderGitlabGroupFieldExtension />
        <ComponentNameFieldExtension />
        <ComponentTypeFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/opsgenie" element={<OpsgeniePage />} />
    <Route path="/tech-radar" element={<TechRadarPage width={1500} height={800} />} />
    <Route path="/ai-tech-radar" element={<AITechRadarPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/frontend-external-docs" element={<FrontendExternalDocsPage />} />
    <Route path="/stats" element={<PlausibleAnalyticsPage />} />
    <Route path="/qa-test-results" element={<QaTestResultsPage />} />
    <Route path="/dora" element={<DoraPage />} />
    <Route path="/tech-insights-overview" element={techInsightsOverviewPage} />
    <Route path="/tasks-observability" element={<TasksObservabilityPage />} />
    <Route path="/contrib" element={<ContribPage />} />
    <Route path="/datadog-service-catalog" element={<DatadogServiceCatalogPage />} />
    <Route path="/testing-metrics" element={<TestingMetricsPage />} />
    <Route path="/chat" element={<McpChatPage />} />
  </FlatRoutes>
)

const convertedRoot = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>
)

const app = createApp({
  features: [
    appVisualizerPlugin,
    authPlugin,
    convertedOptions,
    ...pluginApiOverrides,
    ...convertedRoot
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot
    })
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage
    })
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage
    })
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex
    })
  }
})

export default app.createRoot()
